<script setup>
/* global axios */
// [AstraChat] Pop-up do compositor para ENVIAR mensagens ricas do WhatsApp pela
// caixa AstraCall: produto, contato, Pix, enquete, evento — e votar numa enquete
// recebida. Chama /kanban/connections/send_rich (cria a bolha no timeline e o
// AstraCall entrega o conteúdo nativo). Só aparece em caixas AstraCall (gate no
// ReplyBottomPanel). Voto reusa /poll_vote.
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close']);

const route = useRoute();
const currentChat = useMapGetter('getSelectedChat');
const getInbox = useMapGetter('inboxes/getInbox');
// flags "rich_send" da conexão desta caixa (produto/contato/pix/enquete/evento).
// Ausente = LIGADO. Configurável em Configurações da conexão (provedor AstraCall).
// Começa com o valor do store (instantâneo) e, ao abrir, BUSCA o estado FRESCO do
// backend (by_inbox) — assim o menu sempre reflete o que está salvo, sem depender
// do cache do store (que pode estar velho após um reload).
const richFlags = ref(
  getInbox.value?.(currentChat.value?.inbox_id)?.kanban_rich_send || {}
);
const loadRichFlags = async () => {
  const inboxId = currentChat.value?.inbox_id;
  if (!inboxId) return;
  try {
    const { data } = await axios.get(`${base.value}/by_inbox`, {
      params: { inbox_id: inboxId },
    });
    if (data?.data?.meta?.rich_send) richFlags.value = data.data.meta.rich_send;
  } catch (e) {
    // mantém o fallback do store
  }
};
const base = computed(
  () => `/api/v1/accounts/${route.params.accountId}/kanban/connections`
);
// chaves Pix ficam em /kanban/pix_keys (fora do namespace connections)
const pixKeysBase = computed(
  () => `/api/v1/accounts/${route.params.accountId}/kanban/pix_keys`
);
const convId = computed(() => currentChat.value?.id);

const view = ref('menu');
const sending = ref(false);
const error = ref('');

const product = ref({
  title: '',
  price: '',
  sale_price: '',
  currency: 'R$',
  description: '',
  url: '',
  image_url: '',
});
const pix = ref({
  key: '',
  amount: '',
  merchant_name: '',
  merchant_city: '',
  txid: '',
  description: '',
});

// ---- Contatos (busca no Chatwoot + vários + manual) ----
const contactQuery = ref('');
const contactResults = ref([]);
const selectedContacts = ref([]);
const searchingContacts = ref(false);
const showManualContact = ref(false);
const manualContact = ref({ full_name: '', phone: '' });
let searchTimer = null;
const searchContacts = () => {
  clearTimeout(searchTimer);
  const q = contactQuery.value.trim();
  if (!q) {
    contactResults.value = [];
    return;
  }
  searchTimer = setTimeout(async () => {
    searchingContacts.value = true;
    try {
      const { data } = await axios.get(
        `/api/v1/accounts/${route.params.accountId}/contacts/search`,
        { params: { q } }
      );
      contactResults.value = (data?.payload || [])
        .map(c => ({ id: c.id, full_name: c.name, phone: c.phone_number }))
        .filter(c => c.phone);
    } catch {
      contactResults.value = [];
    } finally {
      searchingContacts.value = false;
    }
  }, 300);
};
const pickContact = c => {
  if (!selectedContacts.value.some(x => x.phone === c.phone)) {
    selectedContacts.value.push({ full_name: c.full_name, phone: c.phone });
  }
  contactQuery.value = '';
  contactResults.value = [];
};
const addManualContact = () => {
  const c = manualContact.value;
  if (!c.full_name.trim() || !c.phone.trim()) return;
  selectedContacts.value.push({
    full_name: c.full_name.trim(),
    phone: c.phone.trim(),
  });
  manualContact.value = { full_name: '', phone: '' };
  showManualContact.value = false;
};
const removeContact = i => selectedContacts.value.splice(i, 1);

// ---- Chaves Pix salvas ----
const pixKeys = ref([]);
const selectedPixKeyId = ref('');
const loadPixKeys = async () => {
  try {
    const { data } = await axios.get(pixKeysBase.value);
    pixKeys.value = data?.data || [];
  } catch {
    pixKeys.value = [];
  }
};
const applyPixKey = () => {
  const k = pixKeys.value.find(x => String(x.id) === String(selectedPixKeyId.value));
  if (!k) return;
  pix.value.key = k.pix_key || '';
  pix.value.merchant_name = k.merchant_name || '';
  pix.value.merchant_city = k.merchant_city || '';
};
onMounted(loadPixKeys);
onMounted(loadRichFlags);
const poll = ref({ name: '', options: ['', ''], multiple_answers: false });
const event = ref({
  name: '',
  description: '',
  location: '',
  join_link: '',
  date: '',
  time: '',
});

const TYPES = [
  {
    key: 'product',
    label: 'Produto',
    icon: 'i-lucide-shopping-bag',
    desc: 'Foto, preço e link',
  },
  {
    key: 'contact',
    label: 'Contato',
    icon: 'i-lucide-user-round',
    desc: 'Compartilhar um contato',
  },
  {
    key: 'pix',
    label: 'Cobrança Pix',
    icon: 'i-lucide-qr-code',
    desc: 'Pix copia e cola',
  },
  {
    key: 'poll',
    label: 'Enquete',
    icon: 'i-lucide-bar-chart-3',
    desc: 'Pergunta com opções',
  },
  {
    key: 'event',
    label: 'Evento',
    icon: 'i-lucide-calendar-days',
    desc: 'Convite de evento',
  },
  {
    key: 'vote',
    label: 'Votar em enquete',
    icon: 'i-lucide-vote',
    desc: 'Responder uma enquete recebida',
  },
];

// tipos habilitados nesta conexão. 'vote' (responder enquete recebida) segue o
// flag de 'poll'. Chave ausente = habilitada.
const enabledTypes = computed(() =>
  TYPES.filter(ty => {
    const key = ty.key === 'vote' ? 'poll' : ty.key;
    return richFlags.value[key] !== false;
  })
);

const titleFor = computed(
  () => TYPES.find(t => t.key === view.value)?.label || 'Enviar item'
);

// enquetes recebidas na conversa (para o fluxo de voto)
const receivedPolls = computed(() =>
  (currentChat.value?.messages || [])
    .filter(m => typeof m.content === 'string' && /^\s*📊/.test(m.content))
    .map(m => ({
      id: (m.content.match(/_PID:\s*([^_]+)_/) || [])[1]?.trim() || m.source_id,
      question: (m.content.match(/📊\s*\*([^*]+)\*/) || [])[1]?.trim() || 'Enquete',
      options: m.content
        .split('\n')
        .map(l => (l.match(/^\s*\d+[.)]\s*(.+)$/) || [])[1])
        .filter(Boolean),
    }))
    .filter(p => p.id)
);
const votePoll = ref(null);

const goBack = () => {
  error.value = '';
  if (view.value === 'vote' && votePoll.value) {
    votePoll.value = null;
    return;
  }
  view.value = 'menu';
};

const post = async (path, body, okMsg) => {
  if (!convId.value) {
    error.value = 'Conversa não encontrada.';
    return;
  }
  sending.value = true;
  error.value = '';
  try {
    await axios.post(`${base.value}/${path}`, {
      conversation_id: convId.value,
      ...body,
    });
    useAlert(okMsg);
    emit('close');
  } catch (e) {
    error.value = e?.response?.data?.error || 'Não consegui enviar agora.';
  } finally {
    sending.value = false;
  }
};

const sendRich = (kind, payload, okMsg) =>
  post('send_rich', { kind, ...payload }, okMsg);

const submitProduct = () => sendRich('product', product.value, 'Produto enviado!');
const submitContact = () => {
  if (!selectedContacts.value.length) {
    error.value = 'Selecione ou adicione ao menos um contato.';
    return;
  }
  sendRich(
    'contact',
    { contacts: selectedContacts.value },
    selectedContacts.value.length > 1 ? 'Contatos enviados!' : 'Contato enviado!'
  );
};
const submitPix = () => sendRich('pix', pix.value, 'Cobrança Pix enviada!');
const submitPoll = () =>
  sendRich(
    'poll',
    { ...poll.value, options: poll.value.options.filter(o => o.trim()) },
    'Enquete enviada!'
  );
const submitEvent = () => {
  if (!event.value.date) {
    error.value = 'Informe a data do evento.';
    return;
  }
  const p = { ...event.value };
  const dt = new Date(`${event.value.date}T${event.value.time || '09:00'}`);
  if (!Number.isNaN(dt.getTime())) p.start_time = Math.floor(dt.getTime() / 1000);
  sendRich('event', p, 'Evento enviado!');
};
const submitVote = optName =>
  post(
    'poll_vote',
    { poll_message_id: votePoll.value.id, selected_names: [optName] },
    'Voto enviado!'
  );

const addOption = () => poll.value.options.push('');
const removeOption = i => {
  if (poll.value.options.length > 2) poll.value.options.splice(i, 1);
};
</script>

<template>
  <teleport to="body">
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
      @click.self="emit('close')"
    >
      <div
        class="w-full max-w-md rounded-xl bg-n-solid-1 border border-n-weak shadow-xl flex flex-col max-h-[85vh]"
      >
        <!-- header -->
        <div class="flex items-center gap-2 px-4 py-3 border-b border-n-weak">
          <button
            v-if="view !== 'menu'"
            type="button"
            class="grid place-items-center size-7 rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
            @click="goBack"
          >
            <span class="i-lucide-arrow-left size-4" />
          </button>
          <h3 class="text-heading-3 text-n-slate-12 flex-1">
            {{ view === 'menu' ? 'Enviar pelo WhatsApp' : titleFor }}
          </h3>
          <button
            type="button"
            class="grid place-items-center size-7 rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <div class="p-4 overflow-y-auto">
          <p
            v-if="error"
            class="mb-3 text-sm text-n-ruby-11 bg-n-ruby-2 rounded-lg px-3 py-2"
          >
            {{ error }}
          </p>

          <!-- MENU -->
          <div v-if="view === 'menu'" class="grid grid-cols-2 gap-2">
            <button
              v-for="t in enabledTypes"
              :key="t.key"
              type="button"
              class="flex flex-col items-start gap-1 p-3 rounded-lg border border-n-weak hover:bg-n-alpha-2 text-left"
              @click="view = t.key"
            >
              <span :class="t.icon" class="size-5 text-n-brand" />
              <span class="text-sm font-medium text-n-slate-12">{{ t.label }}</span>
              <span class="text-xs text-n-slate-11">{{ t.desc }}</span>
            </button>
          </div>

          <!-- PRODUTO -->
          <form
            v-else-if="view === 'product'"
            class="flex flex-col gap-3"
            @submit.prevent="submitProduct"
          >
            <label class="text-sm">
              <span class="text-n-slate-11">Título *</span>
              <input v-model="product.title" class="rich-input" placeholder="Camiseta Astra" />
            </label>
            <div class="grid grid-cols-3 gap-2">
              <label class="text-sm col-span-1">
                <span class="text-n-slate-11">Moeda</span>
                <input v-model="product.currency" class="rich-input" placeholder="R$" />
              </label>
              <label class="text-sm col-span-1">
                <span class="text-n-slate-11">Preço</span>
                <input v-model="product.price" class="rich-input" placeholder="79.90" />
              </label>
              <label class="text-sm col-span-1">
                <span class="text-n-slate-11">Promo</span>
                <input v-model="product.sale_price" class="rich-input" placeholder="59.90" />
              </label>
            </div>
            <label class="text-sm">
              <span class="text-n-slate-11">Descrição</span>
              <textarea v-model="product.description" rows="2" class="rich-input" />
            </label>
            <label class="text-sm">
              <span class="text-n-slate-11">Link do produto</span>
              <input v-model="product.url" class="rich-input" placeholder="https://..." />
            </label>
            <label class="text-sm">
              <span class="text-n-slate-11">Foto (URL)</span>
              <input v-model="product.image_url" class="rich-input" placeholder="https://.../foto.jpg" />
            </label>
            <NextButton type="submit" label="Enviar produto" :is-loading="sending" class="self-end" />
          </form>

          <!-- CONTATO -->
          <form
            v-else-if="view === 'contact'"
            class="flex flex-col gap-3"
            @submit.prevent="submitContact"
          >
            <div v-if="selectedContacts.length" class="flex flex-col gap-1.5">
              <div
                v-for="(c, i) in selectedContacts"
                :key="i"
                class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-alpha-2"
              >
                <span class="i-lucide-user-round size-4 text-n-slate-10 shrink-0" />
                <span class="text-sm text-n-slate-12 flex-1 truncate">
                  {{ c.full_name }} · {{ c.phone }}
                </span>
                <button
                  type="button"
                  class="text-n-slate-10 hover:text-n-ruby-11"
                  @click="removeContact(i)"
                >
                  <span class="i-lucide-x size-4" />
                </button>
              </div>
            </div>

            <div class="relative">
              <input
                v-model="contactQuery"
                class="rich-input"
                placeholder="Buscar contato do Chatwoot…"
                @input="searchContacts"
              />
              <div
                v-if="contactResults.length"
                class="absolute z-10 left-0 right-0 mt-1 max-h-48 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-lg"
              >
                <button
                  v-for="c in contactResults"
                  :key="c.id"
                  type="button"
                  class="flex items-center gap-2 w-full px-3 py-2 text-left text-sm hover:bg-n-alpha-2"
                  @click="pickContact(c)"
                >
                  <span class="text-n-slate-12 truncate">{{ c.full_name || 'Sem nome' }}</span>
                  <span class="text-n-slate-10 ms-auto shrink-0">{{ c.phone }}</span>
                </button>
              </div>
              <span
                v-else-if="searchingContacts"
                class="absolute right-3 top-2.5 text-xs text-n-slate-10"
              >
                buscando…
              </span>
            </div>

            <button
              v-if="!showManualContact"
              type="button"
              class="self-start text-sm text-n-brand hover:underline"
              @click="showManualContact = true"
            >
              + Adicionar um contato novo (manual)
            </button>
            <div v-else class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak">
              <input v-model="manualContact.full_name" class="rich-input !mt-0" placeholder="Nome" />
              <input v-model="manualContact.phone" class="rich-input !mt-0" placeholder="5561999999999" />
              <div class="flex gap-3 justify-end text-sm">
                <button type="button" class="text-n-slate-11" @click="showManualContact = false">
                  Cancelar
                </button>
                <button type="button" class="text-n-brand font-medium" @click="addManualContact">
                  Adicionar
                </button>
              </div>
            </div>

            <NextButton
              type="submit"
              label="Enviar contato(s)"
              :is-loading="sending"
              class="self-end"
            />
          </form>

          <!-- PIX -->
          <form
            v-else-if="view === 'pix'"
            class="flex flex-col gap-3"
            @submit.prevent="submitPix"
          >
            <label v-if="pixKeys.length" class="text-sm">
              <span class="text-n-slate-11">Usar chave salva</span>
              <select
                v-model="selectedPixKeyId"
                class="rich-input"
                @change="applyPixKey"
              >
                <option value="">— nova / manual —</option>
                <option v-for="k in pixKeys" :key="k.id" :value="k.id">
                  {{ k.label || k.pix_key }}
                </option>
              </select>
            </label>
            <label class="text-sm">
              <span class="text-n-slate-11">Chave Pix *</span>
              <input v-model="pix.key" class="rich-input" placeholder="email@dominio.com / CPF / telefone" />
            </label>
            <label class="text-sm">
              <span class="text-n-slate-11">Valor</span>
              <input v-model="pix.amount" class="rich-input" placeholder="49.90" />
            </label>
            <div class="grid grid-cols-2 gap-2">
              <label class="text-sm">
                <span class="text-n-slate-11">Nome recebedor</span>
                <input v-model="pix.merchant_name" class="rich-input" placeholder="Astra Online" />
              </label>
              <label class="text-sm">
                <span class="text-n-slate-11">Cidade</span>
                <input v-model="pix.merchant_city" class="rich-input" placeholder="Sao Paulo" />
              </label>
            </div>
            <label class="text-sm">
              <span class="text-n-slate-11">Descrição</span>
              <input v-model="pix.description" class="rich-input" placeholder="Cobrança do plano" />
            </label>
            <NextButton type="submit" label="Enviar cobrança" :is-loading="sending" class="self-end" />
          </form>

          <!-- ENQUETE -->
          <form
            v-else-if="view === 'poll'"
            class="flex flex-col gap-3"
            @submit.prevent="submitPoll"
          >
            <label class="text-sm">
              <span class="text-n-slate-11">Pergunta *</span>
              <input v-model="poll.name" class="rich-input" placeholder="Qual horário prefere?" />
            </label>
            <div class="flex flex-col gap-2">
              <span class="text-n-slate-11 text-sm">Opções *</span>
              <div v-for="(o, i) in poll.options" :key="i" class="flex items-center gap-2">
                <input v-model="poll.options[i]" class="rich-input" :placeholder="`Opção ${i + 1}`" />
                <button
                  v-if="poll.options.length > 2"
                  type="button"
                  class="grid place-items-center size-8 rounded-lg text-n-slate-11 hover:bg-n-alpha-2 shrink-0"
                  @click="removeOption(i)"
                >
                  <span class="i-lucide-trash-2 size-4" />
                </button>
              </div>
              <button
                type="button"
                class="self-start text-sm text-n-brand hover:underline"
                @click="addOption"
              >
                + Adicionar opção
              </button>
            </div>
            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <input v-model="poll.multiple_answers" type="checkbox" />
              Permitir marcar várias opções
            </label>
            <NextButton type="submit" label="Enviar enquete" :is-loading="sending" class="self-end" />
          </form>

          <!-- EVENTO -->
          <form
            v-else-if="view === 'event'"
            class="flex flex-col gap-3"
            @submit.prevent="submitEvent"
          >
            <label class="text-sm">
              <span class="text-n-slate-11">Nome do evento *</span>
              <input v-model="event.name" class="rich-input" placeholder="Reunião de alinhamento" />
            </label>
            <div class="grid grid-cols-2 gap-2">
              <label class="text-sm">
                <span class="text-n-slate-11">Data *</span>
                <input v-model="event.date" type="date" class="rich-input" />
              </label>
              <label class="text-sm">
                <span class="text-n-slate-11">Hora</span>
                <input v-model="event.time" type="time" class="rich-input" />
              </label>
            </div>
            <label class="text-sm">
              <span class="text-n-slate-11">Local</span>
              <input v-model="event.location" class="rich-input" placeholder="Escritório" />
            </label>
            <label class="text-sm">
              <span class="text-n-slate-11">Link da call</span>
              <input v-model="event.join_link" class="rich-input" placeholder="https://meet.google.com/..." />
            </label>
            <label class="text-sm">
              <span class="text-n-slate-11">Descrição</span>
              <textarea v-model="event.description" rows="2" class="rich-input" />
            </label>
            <NextButton type="submit" label="Enviar evento" :is-loading="sending" class="self-end" />
          </form>

          <!-- VOTO -->
          <div v-else-if="view === 'vote'" class="flex flex-col gap-3">
            <template v-if="!votePoll">
              <p v-if="!receivedPolls.length" class="text-sm text-n-slate-11">
                Nenhuma enquete recebida nesta conversa. Você também pode votar
                clicando direto nas opções da enquete no histórico.
              </p>
              <button
                v-for="(p, i) in receivedPolls"
                :key="i"
                type="button"
                class="flex items-center gap-2 p-3 rounded-lg border border-n-weak hover:bg-n-alpha-2 text-left"
                @click="votePoll = p"
              >
                <span class="i-lucide-bar-chart-3 size-4 text-n-brand shrink-0" />
                <span class="text-sm text-n-slate-12">{{ p.question }}</span>
              </button>
            </template>
            <template v-else>
              <p class="text-sm font-medium text-n-slate-12">{{ votePoll.question }}</p>
              <button
                v-for="(opt, i) in votePoll.options"
                :key="i"
                type="button"
                class="p-2.5 rounded-lg border border-n-weak hover:bg-n-alpha-2 text-left text-sm text-n-slate-12 disabled:opacity-60"
                :disabled="sending"
                @click="submitVote(opt)"
              >
                {{ opt }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<style scoped>
.rich-input {
  @apply block w-full mt-1 px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1 text-sm text-n-slate-12 outline-none focus:border-n-brand;
}
</style>
